import React, { useCallback } from 'react';
import { X } from 'lucide-react';
import {
    fetchGeoSeoSignups,
    fetchTraditionalSeoSignups,
    fetchChannelAnalytics,
} from '../api/analyticsApi';
import ChannelSeoPanel from './ChannelSeoPanel';
import AgentSignupPanel from './AgentSignupPanel';
import OpenHomeSignupPanel from './OpenHomeSignupPanel';

const GEO_SOURCES = ['geo_seo', 'local_seo'];
const AGENT_SOURCE = 'real_estate_agent';

const helpFor = (source) => {
    const src = source?.utm_source?.toLowerCase();
    if (GEO_SOURCES.includes(src)) {
        return {
            helpText: 'Tracks buyer portal traffic from geo SEO suburb pages — same attribution model as paid social.',
            utmExample: '?utm_source=geo_seo&utm_campaign=<suburb>',
        };
    }
    if (src === 'traditional_seo') {
        return {
            helpText: 'Tracks Google organic search traffic to the buyer portal (main website SEO, not geo suburb pages).',
            utmExample: '?utm_source=google&utm_medium=organic&utm_campaign=<page>',
        };
    }
    const medium = source?.utm_medium ? ` / ${source.utm_medium}` : '';
    return {
        helpText: `Visitors, signups, enquiries, property views and attributed revenue for traffic tagged ${source?.utm_source}${medium}. "By page / campaign" groups rows by their utm_campaign.`,
        utmExample: `?utm_source=${source?.utm_source || '<source>'}${source?.utm_medium ? `&utm_medium=${source.utm_medium}` : ''}&utm_campaign=<campaign>`,
    };
};

/**
 * Drill-down modal for a single Traffic Sources row. Renders the same
 * per-channel dashboard (totals + per-campaign + keyword breakdown) for ANY
 * source — the SEO channels use their dedicated endpoints, everything else
 * uses the generic /channel endpoint scoped to utm_source (+ medium).
 */
const SourceDetailModal = ({ isOpen, source, startDate, endDate, onClose }) => {
    // Memoised so ChannelSeoPanel's effect (which depends on fetchData) doesn't
    // refetch every render — re-created only when the selected source changes.
    const fetchData = useCallback(
        (s, e) => {
            const src = source?.utm_source?.toLowerCase();
            if (GEO_SOURCES.includes(src)) return fetchGeoSeoSignups(s, e);
            if (src === 'traditional_seo') return fetchTraditionalSeoSignups(s, e);
            return fetchChannelAnalytics(source?.utm_source, source?.utm_medium, s, e, source?.label);
        },
        [source],
    );

    if (!isOpen || !source) return null;

    const { label, accent = '#55C5D0', Icon } = source;
    const { helpText, utmExample } = helpFor(source);

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b border-gray-200">
                    <div className="flex items-center gap-3 min-w-0">
                        <span
                            className="flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0 text-white"
                            style={source.style || { background: accent }}
                        >
                            {Icon
                                ? <Icon size={16} />
                                : <span className="text-[11px] font-bold">{label?.slice(0, 2).toUpperCase()}</span>}
                        </span>
                        <div className="min-w-0">
                            <h3 className="text-lg font-bold text-gray-900 leading-tight">{label}</h3>
                            {source.utm_medium && (
                                <p className="text-xs text-gray-500 mt-0.5 capitalize truncate">{source.utm_medium}</p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>
                <div className="p-5 overflow-y-auto">
                    {source?.utm_source?.toLowerCase() === AGENT_SOURCE ? (
                        <div className="space-y-6">
                            <AgentSignupPanel
                                startDate={startDate}
                                endDate={endDate}
                                accent={accent}
                            />
                            {/* Open-home QR / invite-email sign-ups, grouped by agent. */}
                            <OpenHomeSignupPanel
                                startDate={startDate}
                                endDate={endDate}
                            />
                        </div>
                    ) : (
                        <ChannelSeoPanel
                            title={label}
                            fetchData={fetchData}
                            errorLabel={`${label} analytics`}
                            helpText={helpText}
                            utmExample={utmExample}
                            accent={accent}
                            startDate={startDate}
                            endDate={endDate}
                            embedded
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default SourceDetailModal;
